import { Cart } from "@/lib/shopify/types";
import { siteConfig } from "@/lib/seo-config";

export const BAG_ENABLED = process.env.NEXT_PUBLIC_FEATURE_CART === "true";

// Mirrors the Shopify automatic discount ("50% off your 2nd book and up, same
// kind": printed + printed or digital + digital — formats never mix). The
// discount itself is applied by Shopify; these only drive the bag's copy, so
// if the Shopify admin setup changes, they must change with it.
// Shipping is intentionally NOT modelled here — it's calculated by Shopify at
// checkout, so the bag shows merchandise totals only.
export const BAG_DISCOUNT_PERCENT = 50;
/** Most copies of one book a shopper can add from the unlock page. */
export const MAX_BOOK_QUANTITY = 10;
/** Inverse of multiCopyCents: the full (undiscounted) price of `qty` copies, from what was charged. */
export function multiCopyListCents(chargedCents: number, qty: number): number {
  if (qty <= 1) return chargedCents;
  const guess = chargedCents / (1 + (qty - 1) / 2);
  for (let u = Math.floor(guess) - 2; u <= Math.ceil(guess) + 2; u++) {
    if (multiCopyCents(u, qty) === chargedCents) return u * qty;
  }
  return chargedCents;
}
/** Cents for `qty` copies of one book: the 1st at full price, each further copy at the discount. */
export function multiCopyCents(unitCents: number, qty: number): number {
  const discountedUnit = Math.round((unitCents * (100 - BAG_DISCOUNT_PERCENT)) / 100);
  return unitCents + Math.max(qty - 1, 0) * discountedUnit;
}

export const DIGITAL_VARIANT_ID =
  process.env.NEXT_PUBLIC_SHOPIFY_COMIX_DIGITAL_VARIANT_ID ||
  process.env.NEXT_PUBLIC_SHOPIFY_COMIX_VARIANT_ID ||
  "";
export const PRINTED_VARIANT_ID =
  process.env.NEXT_PUBLIC_SHOPIFY_COMIX_PRINTED_VARIANT_ID ||
  process.env.NEXT_PUBLIC_SHOPIFY_COMIX_VARIANT_ID ||
  "";
// Discounted printed book offered as a post-purchase upsell ("Get it printed
// too!" on /comix/unlock). Still fulfilled as a printed book (_product stays
// "printed"); only the variant/price differ from the standalone printed book.
export const UPSELL_PRINTED_VARIANT_ID =
  process.env.NEXT_PUBLIC_SHOPIFY_COMIX_PHYSICAL_20_VARIANT_ID ||
  PRINTED_VARIANT_ID ||
  "";

export function lineAttr(
  line: { attributes?: { key: string; value: string }[] },
  key: string,
): string | undefined {
  return line.attributes?.find((a) => a.key === key)?.value;
}

export function isDigitalLine(line: {
  attributes?: { key: string; value: string }[];
}): boolean {
  return lineAttr(line, "_product") === "digital";
}

export type BagKind = "printed" | "digital";

export interface BagTotals {
  /** Undiscounted merchandise subtotal — drives the promo progress bar. */
  subtotal: number;
  /** Merchandise total after Shopify's automatic discount. */
  discountedSubtotal: number;
  discountActive: boolean;
  /**
   * Formats with exactly one book in the bag — adding a 2nd of that same
   * format is what unlocks the discount. Empty once every format present
   * already has a 2nd book.
   */
  upsellKinds: BagKind[];
  /** Formats with 2+ books in the bag — the ones the discount currently applies to. */
  discountKinds: BagKind[];
  /** Order (merchandise) savings from the automatic discount. */
  totalSavings: number;
  /** Merchandise total shown in the bag. Shipping is added by Shopify at checkout. */
  total: number;
  totalBeforeDiscount: number;
}

export function computeBagTotals(cart: Cart): BagTotals {
  const subtotal = parseFloat(cart.cost.subtotalAmount.amount || "0");
  const discountedSubtotal = parseFloat(cart.cost.totalAmount.amount || "0");
  const discountActive = discountedSubtotal < subtotal - 0.001;

  const qty: Record<BagKind, number> = { printed: 0, digital: 0 };
  for (const line of cart.lines) {
    qty[isDigitalLine(line) ? "digital" : "printed"] += line.quantity;
  }
  const upsellKinds = (["printed", "digital"] as const).filter((k) => qty[k] === 1);
  const discountKinds = (["printed", "digital"] as const).filter((k) => qty[k] >= 2);

  return {
    subtotal,
    discountedSubtotal,
    discountActive,
    upsellKinds,
    discountKinds,
    totalSavings: subtotal - discountedSubtotal,
    total: discountedSubtotal,
    totalBeforeDiscount: subtotal,
  };
}

/**
 * Per-line savings from "50% off your 2nd book and up, same kind only". Within each
 * kind (printed / digital) the single most expensive book stays full price and every
 * other book is BAG_DISCOUNT_PERCENT off. Returns line id -> savings in dollars.
 */
export function computeLineDiscounts(cart: Cart, actualSavings?: number): Record<string, number> {
  const out: Record<string, number> = {};
  for (const kind of ["printed", "digital"] as const) {
    const lines = cart.lines.filter((l) => (isDigitalLine(l) ? "digital" : "printed") === kind);
    const units = lines.flatMap((l) => {
      const unit = parseFloat(l.cost.subtotalAmount?.amount || "0") / Math.max(l.quantity, 1);
      return Array.from({ length: l.quantity }, () => ({ id: l.id, unit }));
    });
    units.sort((a, b) => b.unit - a.unit);
    units.slice(1).forEach((u) => {
      out[u.id] = (out[u.id] ?? 0) + (u.unit * BAG_DISCOUNT_PERCENT) / 100;
    });
  }
  if (actualSavings === undefined) return out;
  // Shopify decides what is really discounted: show nothing while it isn't applying a
  // discount, and scale ours so the lines always add up to the savings in the total.
  const raw = Object.values(out).reduce((n, v) => n + v, 0);
  if (actualSavings <= 0.001 || raw <= 0) return {};
  const ids = Object.keys(out);
  const cents: Record<string, number> = {};
  let left = Math.round(actualSavings * 100);
  ids.forEach((id, i) => {
    cents[id] = i === ids.length - 1 ? left : Math.min(left, Math.round((out[id] / raw) * actualSavings * 100));
    left -= cents[id];
  });
  return Object.fromEntries(ids.map((id) => [id, cents[id] / 100]));
}

/**
 * Builds the Shopify line-item attributes for a comix run. The webhook
 * (order-paid) and the bag UI both depend on these keys — keep in sync there.
 */
export function buildComixLineAttributes(opts: {
  run: {
    sessionId?: string | null;
    userEmail?: string | null;
    generatedImages?: string[];
    config?: Record<string, any> | null;
  };
  runId: string;
  product: string;
  fallbackSessionId?: string;
  characterPhotoUrls?: string[];
}): { key: string; value: string }[] {
  const { run, runId, product, fallbackSessionId, characterPhotoUrls = [] } = opts;
  const config = (run.config as Record<string, any>) || {};
  const coverImage = config.coverImage || config.finishedBook?.coverImage;
  const allImages = [
    ...(coverImage ? [coverImage] : []),
    ...(run.generatedImages || []),
  ];
  const activeSessionId = run.sessionId || fallbackSessionId || "";
  const storyId = config.storyId || "";
  const storyTitle = config.storyHeadline || config.storyTemplate || "";
  const baseUrl = siteConfig.url;
  const previewUrl = `${baseUrl}/comix/preview?sessionId=${activeSessionId}&storyId=${storyId}`;

  return [
    { key: "_generated", value: allImages.join(",") },
    { key: "_sessionId", value: activeSessionId },
    { key: "_runId", value: runId },
    { key: "_productType", value: "comix" },
    { key: "_product", value: product },
    { key: "_previewUrl", value: previewUrl },
    ...(run.userEmail ? [{ key: "_customerEmail", value: run.userEmail }] : []),
    ...(storyId ? [{ key: "_storyId", value: storyId }] : []),
    ...(characterPhotoUrls.length > 0
      ? [{ key: "_characterPhotos", value: characterPhotoUrls.join(",") }]
      : []),
    ...(storyTitle ? [{ key: "_title", value: storyTitle }] : []),
    ...(coverImage ? [{ key: "_coverImage", value: coverImage }] : []),
  ];
}

/**
 * Cart line attributes are writable by anyone holding the public Storefront
 * token, so URLs read from them must never reach href/src unsanitized
 * (blocks javascript:, data:, etc.).
 */
export function safeHttpUrl(url: string | undefined): string | undefined {
  if (!url) return undefined;
  try {
    const parsed = new URL(url);
    if (parsed.protocol === "http:" || parsed.protocol === "https:") {
      return url;
    }
  } catch {
    // not an absolute URL
  }
  return undefined;
}

export function formatUsd(amount: number): string {
  const rounded = Math.round(amount * 100) / 100;
  return Number.isInteger(rounded) ? `$${rounded}` : `$${rounded.toFixed(2)}`;
}
